import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, Users, FolderOpen, Crown, Sparkles, 
  Circle, Trash2, Loader2, BarChart3, RotateCcw, Cpu, UserPlus, Eye, EyeOff,
  ArrowUpDown, ArrowUp, ArrowDown, Bug
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "https://smartballoon-1.onrender.com/api";

// Sort icon component - defined outside to avoid re-creation on each render
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  return sortDirection === 'asc' 
    ? <ArrowUp className="w-3 h-3 ml-1" /> 
    : <ArrowDown className="w-3 h-3 ml-1" />;
};

const AdminPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingPlan, setUpdatingPlan] = useState(null);
  const [resettingAI, setResettingAI] = useState(null);
  
  // Create User Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    name: '',
    plan: 'AI'  // Default to AI plan (FREE/PRO removed)
  });
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [deletingUser, setDeletingUser] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const loadData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/stats`, getAuthHeaders())
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (userId, newPlan) => {
    setUpdatingPlan(userId);
    try {
      await axios.put(
        `${API_URL}/admin/users/${userId}/plan?plan=${newPlan}`,
        {},
        getAuthHeaders()
      );
      setUsers(prevUsers => prevUsers.map(u => u.id === userId ? { ...u, plan: newPlan } : u));
      toast.success(`Piano aggiornato a ${newPlan}`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento del piano');
    } finally {
      setUpdatingPlan(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const displayName = userName || 'questo utente';
    if (!window.confirm(`Sei sicuro di voler eliminare ${displayName}? Tutti i suoi progetti verranno eliminati.`)) {
      return;
    }
    
    setDeletingUser(userId);
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, getAuthHeaders());
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
      toast.success('Utente eliminato con successo');
      // Refresh stats after deletion
      const statsRes = await axios.get(`${API_URL}/admin/stats`, getAuthHeaders());
      setStats(statsRes.data);
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente');
    } finally {
      setDeletingUser(null);
    }
  };
  
  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = (a.name || a.email || '').toLowerCase();
          bVal = (b.name || b.email || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'plan':
          const planOrder = { 'AI_UNLIMITED': 4, 'AI': 3, 'AI_TRIAL': 2, 'PRO': 1, 'FREE': 0 };
          aVal = planOrder[a.plan] || 0;
          bVal = planOrder[b.plan] || 0;
          break;
        case 'smartballoon_count':
          aVal = a.smartballoon_projects_count || 0;
          bVal = b.smartballoon_projects_count || 0;
          break;
        case 'smartrevision_count':
          aVal = a.smartrevision_projects_count || 0;
          bVal = b.smartrevision_projects_count || 0;
          break;
        case 'ai_usage_count':
          aVal = a.ai_usage_count || 0;
          bVal = b.ai_usage_count || 0;
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortField, sortDirection]);

  const handleResetAIUsage = async (userId, userName) => {
    if (!window.confirm(`Azzerare gli utilizzi AI per ${userName}?`)) {
      return;
    }
    
    setResettingAI(userId);
    try {
      const response = await axios.post(
        `${API_URL}/admin/users/${userId}/reset-ai`,
        {},
        getAuthHeaders()
      );
      
      // Update local state
      setUsers(prevUsers => prevUsers.map(u => u.id === userId ? { ...u, ai_usage_count: 0 } : u));
      toast.success(`Utilizzi AI azzerati per ${userName} (erano ${response.data.previous_count})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset AI');
    } finally {
      setResettingAI(null);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password) {
      toast.error('Email e password sono obbligatorie');
      return;
    }
    
    if (newUserData.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    
    setCreateLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/users/create`,
        newUserData,
        getAuthHeaders()
      );
      
      toast.success(response.data.message || 'Utente creato con successo');
      setShowCreateDialog(false);
      setNewUserData({ email: '', password: '', name: '', plan: 'AI' });
      loadData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione utente');
    } finally {
      setCreateLoading(false);
    }
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'AI':
        return <Badge className="bg-amber-500"><Sparkles className="w-3 h-3 mr-1" />AI</Badge>;
      case 'PRO':
        return <Badge className="bg-blue-500"><Crown className="w-3 h-3 mr-1" />PRO</Badge>;
      default:
        return <Badge variant="secondary"><Circle className="w-3 h-3 mr-1" />FREE</Badge>;
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="h-14 border-b border-slate-200 bg-white flex items-center px-6 justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <h1 className="font-bold text-slate-900">Pannello Amministratore</h1>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate('/meta-debug')}
          data-testid="meta-debug-link"
          className="text-purple-600 border-purple-200 hover:bg-purple-50"
        >
          <Bug className="w-4 h-4 mr-2" />
          Meta Pixel Debug
        </Button>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid md:grid-cols-5 gap-4 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Utenti Totali
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Progetti Totali
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.total_projects || 0}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-blue-500" />
                    Utenti PRO
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {stats?.users_by_plan?.PRO || 0}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    Utenti AI
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-amber-600">
                    {stats?.users_by_plan?.AI || 0}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-purple-500" />
                    Utenti AI Unlimited
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">
                    {stats?.users_by_plan?.AI_UNLIMITED || 0}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-green-500" />
                    Utenti Trial
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {stats?.users_by_plan?.AI_TRIAL || 0}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Users Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Gestione Utenti
                </CardTitle>
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crea Utente
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Nome
                          <SortIcon field="name" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center">
                          Email
                          <SortIcon field="email" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('plan')}
                      >
                        <div className="flex items-center">
                          Piano
                          <SortIcon field="plan" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Giorni / Rinnovo</TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('ai_usage_count')}
                      >
                        <div className="flex items-center justify-center">
                          AI Usage
                          <SortIcon field="ai_usage_count" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('smartballoon_count')}
                      >
                        <div className="flex items-center justify-center">
                          <span className="text-xs">SmartBalloon</span>
                          <SortIcon field="smartballoon_count" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('smartrevision_count')}
                      >
                        <div className="flex items-center justify-center">
                          <span className="text-xs">SmartRevision</span>
                          <SortIcon field="smartrevision_count" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center">
                          Data Registrazione
                          <SortIcon field="created_at" sortField={sortField} sortDirection={sortDirection} />
                        </div>
                      </TableHead>
                      <TableHead>Lingua</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">
                          {u.name || <span className="text-slate-400 italic">-</span>}
                          {u.is_admin && (
                            <Badge variant="outline" className="ml-2 text-xs">Admin</Badge>
                          )}
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Select
                            value={u.plan}
                            onValueChange={(value) => handlePlanChange(u.id, value)}
                            disabled={updatingPlan === u.id}
                          >
                            <SelectTrigger className="w-[140px]" data-testid={`plan-select-${u.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FREE">
                                <span className="flex items-center gap-1">
                                  <Circle className="w-3 h-3" /> FREE
                                </span>
                              </SelectItem>
                              <SelectItem value="PRO">
                                <span className="flex items-center gap-1">
                                  <Crown className="w-3 h-3 text-blue-500" /> PRO
                                </span>
                              </SelectItem>
                              <SelectItem value="AI">
                                <span className="flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-amber-500" /> AI
                                </span>
                              </SelectItem>
                              <SelectItem value="AI_UNLIMITED">
                                <span className="flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-purple-500" /> AI_UNLIMITED
                                </span>
                              </SelectItem>
                              <SelectItem value="AI_TRIAL">
                                <span className="flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-green-500" /> TRIAL
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          {u.plan === 'AI_TRIAL' ? (
                            <span className="text-sm">
                              {u.trial_end_date ? (() => {
                                const trialEnd = new Date(u.trial_end_date);
                                const now = new Date();
                                const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
                                if (daysLeft <= 0) return <span className="text-red-600 font-medium">Scaduto</span>;
                                return <span className="text-green-600 font-medium">{daysLeft}gg</span>;
                              })() : <span className="text-slate-400">-</span>}
                            </span>
                          ) : ['AI', 'AI_UNLIMITED'].includes(u.plan) ? (
                            <span className="text-sm text-slate-500">
                              {u.subscription_end_date ? new Date(u.subscription_end_date).toLocaleDateString('it-IT') : 'Attivo'}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(u.plan) ? (
                            <div className="flex items-center justify-center gap-2">
                              <span className="font-mono text-sm">
                                {u.ai_usage_count || 0}
                                {u.plan === 'AI' ? '/50' : ''}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleResetAIUsage(u.id, u.name || u.email)}
                                disabled={resettingAI === u.id}
                                title="Azzera utilizzi AI"
                                data-testid={`reset-ai-${u.id}`}
                              >
                                {resettingAI === u.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="font-mono text-sm">{u.smartballoon_projects_count || 0}</span>
                            <span className={`text-xs ${u.has_smartballoon_drawings ? 'text-green-600' : 'text-slate-400'}`}>
                              {u.has_smartballoon_drawings ? '✓ Disegni' : '○ No disegni'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="font-mono text-sm">{u.smartrevision_projects_count || 0}</span>
                            <span className={`text-xs ${u.has_smartrevision_drawings ? 'text-green-600' : 'text-slate-400'}`}>
                              {u.has_smartrevision_drawings ? '✓ Disegni' : '○ No disegni'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(u.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell className="uppercase text-sm">
                          {u.language}
                        </TableCell>
                        <TableCell className="text-right">
                          {!u.is_admin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteUser(u.id, u.name || u.email)}
                              disabled={deletingUser === u.id}
                              data-testid={`delete-user-${u.id}`}
                            >
                              {deletingUser === u.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Crea Nuovo Utente
            </DialogTitle>
            <DialogDescription>
              Crea un account per un nuovo utente. L&apos;utente potrà accedere immediatamente con le credenziali fornite.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Email *</Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="email@esempio.com"
                className="mt-1"
                data-testid="new-user-email"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Password *</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  placeholder="Minimo 6 caratteri"
                  className="pr-10"
                  data-testid="new-user-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Nome (opzionale)</Label>
              <Input
                type="text"
                value={newUserData.name}
                onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                placeholder="Nome utente"
                className="mt-1"
                data-testid="new-user-name"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Piano</Label>
              <Select
                value={newUserData.plan}
                onValueChange={(value) => setNewUserData({ ...newUserData, plan: value })}
              >
                <SelectTrigger className="mt-1" data-testid="new-user-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FREE">
                    <span className="flex items-center gap-1">
                      <Circle className="w-3 h-3" /> FREE
                    </span>
                  </SelectItem>
                  <SelectItem value="PRO">
                    <span className="flex items-center gap-1">
                      <Crown className="w-3 h-3 text-blue-500" /> PRO
                    </span>
                  </SelectItem>
                  <SelectItem value="AI">
                    <span className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-amber-500" /> AI
                    </span>
                  </SelectItem>
                  <SelectItem value="AI_UNLIMITED">
                    <span className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-purple-500" /> AI_UNLIMITED
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateDialog(false);
                setNewUserData({ email: '', password: '', name: '', plan: 'AI' });
              }}
            >
              Annulla
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={createLoading || !newUserData.email || !newUserData.password}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-create-user"
            >
              {createLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crea Utente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
